import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  DescriptionOutlined as FileIcon,
  LocationOnOutlined as LocationIcon,
  OpenInNewOutlined as OpenIcon,
  ReceiptLongOutlined as InvoiceIcon,
} from '@mui/icons-material';
import { getDeliveryByPickupOrder } from '../../services/delivery.service';
import type { DeliveryDetails, DeliveryEvidenceFile } from '../../types/delivery.type';
import DialogHeader from '../common/DialogHeader';
import { Detail, PhotoGallery, dedupeByAssetId } from './EvidenceSections';

interface DeliveryEvidenceDialogProps {
  open: boolean;
  pickupOrderId: string | null;
  onClose: () => void;
}


export default function DeliveryEvidenceDialog({ open, pickupOrderId, onClose }: DeliveryEvidenceDialogProps) {
  const { t, i18n } = useTranslation();
  const [details, setDetails] = useState<DeliveryDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const number = useMemo(
    () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language, i18n.resolvedLanguage],
  );

  useEffect(() => {
    if (!open || !pickupOrderId) return;
    let active = true;
    setLoading(true);
    setError(null);
    setDetails(null);
    getDeliveryByPickupOrder(pickupOrderId)
      .then((result) => { if (active) setDetails(result); })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.detail ?? t('monitoring.evidence.loadError'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, pickupOrderId, t]);

  const beforePhotos = useMemo(
    () => dedupeByAssetId(details?.events.flatMap((event) => event.enriched_data.before_photos ?? []) ?? []),
    [details],
  );
  const afterPhotos = useMemo(
    () => dedupeByAssetId(details?.events.flatMap((event) => event.enriched_data.after_photos ?? []) ?? []),
    [details],
  );
  const recyclerPhotos = useMemo(
    () => dedupeByAssetId(details?.events.flatMap((event) => event.enriched_data.recycler_photos ?? []) ?? []),
    [details],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogHeader
        title={t('monitoring.evidence.title')}
        subtitle={pickupOrderId ? `#${pickupOrderId.slice(0, 8)}` : undefined}
      />
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && details && (
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Detail icon={<InvoiceIcon />} label={t('monitoring.evidence.status')}>
                  <Chip size="small" label={details.delivery.current_phase.replace(/_/g, ' ')} sx={{ textTransform: 'capitalize' }} />
                </Detail>
                {details.recycler && (
                  <Detail icon={<LocationIcon />} label={t('monitoring.evidence.recycler')}>
                    {details.recycler.name}{' '}
                    <Link href={details.recycler.google_maps_url} target="_blank" rel="noopener noreferrer">
                      <OpenIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  </Detail>
                )}
              </Stack>
            </Paper>

            <Divider />
            <PhotoGallery title={t('monitoring.evidence.beforePhotos')} photos={beforePhotos} />
            <PhotoGallery title={t('monitoring.evidence.afterPhotos')} photos={afterPhotos} />
            <PhotoGallery title={t('monitoring.evidence.recyclerPhotos')} photos={recyclerPhotos} />

            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('monitoring.evidence.weightInvoice')}</Typography>
              {details.weight_invoice ? (
                <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: details.weight_invoice.photo ? 2 : 0 }}>
                    <Detail icon={<InvoiceIcon />} label={t('monitoring.evidence.serialNumber')}>{details.weight_invoice.serial_number}</Detail>
                    {details.weight_invoice.total_weight != null && (
                      <Detail icon={<InvoiceIcon />} label={t('monitoring.evidence.totalWeight')}>{t('monitoring.quantityKg', { value: number.format(details.weight_invoice.total_weight) })}</Detail>
                    )}
                    {details.weight_invoice.total_price != null && (
                      <Detail icon={<InvoiceIcon />} label={t('monitoring.evidence.totalPrice')}>{number.format(details.weight_invoice.total_price)}</Detail>
                    )}
                  </Stack>
                  {details.weight_invoice.photo && (
                    <Box sx={{ maxWidth: 220 }}>
                      <Link href={details.weight_invoice.photo.download_url ?? undefined} target="_blank" rel="noopener noreferrer">
                        <Box
                          component="img"
                          src={details.weight_invoice.photo.download_url ?? undefined}
                          alt={t('monitoring.evidence.weightInvoice')}
                          sx={{ width: '100%', borderRadius: 1, display: 'block' }}
                        />
                      </Link>
                    </Box>
                  )}
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">{t('monitoring.evidence.noInvoice')}</Typography>
              )}
            </Box>

            {details.pickup_order.files.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('monitoring.evidence.otherFiles')}</Typography>
                <Stack spacing={1}>
                  {details.pickup_order.files.filter((file) => file.download_url).map((file) => (
                    <Button
                      key={file.asset_id}
                      variant="outlined"
                      size="small"
                      startIcon={<FileIcon />}
                      href={file.download_url as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ justifyContent: 'flex-start', width: 'fit-content' }}
                    >
                      {file.filename ?? file.asset_id}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
